/**
 * Unified Authentication & Authorization Hook
 * Single source of truth for all auth/permission logic
 * 
 * Features:
 * - Centralized auth state management
 * - Consistent permission checking
 * - Role-based access control
 * - Clean API with memoization
 * 
 * @author Employee Management System
 * @version 3.0.0
 */

import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { ROLES } from '../api/constants';
import { 
  selectUser, 
  selectUserRole, 
  selectUserPermissions,
  selectTenantModules,
  selectIsAuthenticated
} from '../store/slices/authSlice';
import TokenService from '../services/tokenService';
import { parseJwt } from '../utils/jwtUtils';

// Permission format standardization
const PERMISSION_FORMAT = {
  PREFIX: 'permission.',
  SEPARATOR: '.',
  WILDCARD: '.*'
};

// Standard modules mapping - normalized without permission prefix
const MODULE_PERMISSIONS = {
  campaigns: ['campaigns.read', 'campaigns.write', 'campaigns.delete'],
  draws: ['draws.read', 'draws.write', 'draws.delete'], 
  announcements: ['announcements.read', 'announcements.write', 'announcements.delete'],
  surveys: ['surveys.read', 'surveys.write', 'surveys.delete'],
  brands: ['brands.read', 'brands.write', 'brands.delete'],
  users: ['users.read', 'users.write', 'users.delete'],
  reports: ['reports.read', 'reports.write'],
  tickets: ['tickets.read', 'tickets.write'],
  companies: ['companies.read', 'companies.write', 'companies.delete'],
  subTenants: ['subTenants.read', 'subTenants.write', 'subTenants.delete'],
  payments: ['payments.read', 'payments.write', 'payments.delete'],
  gifts: ['gifts.read', 'gifts.write', 'gifts.delete'],
  dashboard: ['dashboard.read'],
  videos: ['videos.read', 'videos.write', 'videos.delete']
};

/**
 * Unified Authentication Hook
 * Single source of truth for all auth/permission operations
 */
const useAuth = () => {
  // Core auth state
  const user = useSelector(selectUser);
  const userRole = useSelector(selectUserRole);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const rawPermissions = useSelector(selectUserPermissions);
  const tenantModules = useSelector(selectTenantModules);

  // **TOKEN-BASED DATA EXTRACTION**
  // Get modules and permissions directly from JWT token (most up-to-date source)
  const tokenData = useMemo(() => {
    const token = TokenService.getAccessToken();
    if (!token || TokenService.isTokenExpired(token)) {
      return { modules: [], permissions: [] };
    }

    try {
      const decoded = parseJwt(token);
      if (!decoded) return { modules: [], permissions: [] };

      return {
        modules: decoded.Modules || [],
        permissions: decoded.Permissions || [],
        role: decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"],
        tenantId: decoded.TenantId,
        userId: decoded.Id || decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"]
      };
    } catch (error) {
      console.error('🔥 useAuth: Token decode error:', error);
      return { modules: [], permissions: [] };
    }
  }, []);

  // Use token data as primary source, fallback to Redux state (memoized)
  const effectiveModules = useMemo(() => (
    (tokenData.modules && tokenData.modules.length > 0)
      ? tokenData.modules
      : (tenantModules || [])
  ), [tokenData.modules, tenantModules]);

  const effectivePermissions = useMemo(() => (
    (tokenData.permissions && tokenData.permissions.length > 0)
      ? tokenData.permissions
      : (rawPermissions || [])
  ), [tokenData.permissions, rawPermissions]);

  // Normalize permissions to consistent format (using effective permissions from token)
  const normalizedPermissions = useMemo(() => {
    if (!effectivePermissions || !Array.isArray(effectivePermissions)) return [];
    
    return effectivePermissions.map(permission => {
      // AuthService already normalizes permissions, but double-check for safety
      const cleaned = permission.startsWith(PERMISSION_FORMAT.PREFIX) 
        ? permission.substring(PERMISSION_FORMAT.PREFIX.length)
        : permission;
      
      return cleaned.toLowerCase().trim();
    });
  }, [effectivePermissions]);

  // Simplified: User permissions are exactly what comes from JWT (normalized)
  // No complex effectivePermissions calculation needed

  // Core permission checking functions
  const auth = useMemo(() => {
    /**
     * [TEMEL YETKİ FONKSİYONU]
     * Kullanıcının belirli bir izne sahip olup olmadığını kontrol eder.
     * @param {string} permission - Permission to check (e.g., 'campaigns.read')
     * @returns {boolean}
     */
    const hasPermission = (permission) => {
      if (!permission || !isAuthenticated) return false;

      // Super Admin her izne sahiptir
      if (userRole === ROLES.ADMIN) return true;

      // TenantAdmin'in tüm izinlere sahip olduğunu varsayıyoruz (modül kısıtlaması hariç)
      if (userRole === ROLES.TENANT_ADMIN) return true;
      
      // User rolü için, Redux'tan gelen izin listesini kontrol et
      if (userRole === ROLES.USER) {
        const normalizedPerm = (permission.startsWith('permission.') 
          ? permission.substring(11) 
          : permission).toLowerCase().trim(); // 🔧 FIX: Lowercase for case-insensitive comparison
        
        // 1. Doğrudan eşleşme var mı? (örn: 'campaigns.read')
        if (normalizedPermissions.includes(normalizedPerm)) return true;
        
        // 2. Wildcard eşleşmesi var mı? (örn: 'campaigns.*')
        const [module] = normalizedPerm.split('.');
        return normalizedPermissions.includes(`${module}.*`);
      }

      return false;
    };

    const hasAnyPermission = (required = []) => required.some(p => hasPermission(p));
    const hasAllPermissions = (required = []) => required.every(p => hasPermission(p));

    /**
     * [MODÜL ERİŞİM FONKSİYONU]
     * Bir kullanıcının bir modüle genel olarak erişip erişemeyeceğini belirler.
     * Bu, hem Tenant'ın modüle üyeliğini hem de kullanıcının o modülle ilgili bir izni olup olmadığını kontrol eder.
     */
    const canAccessModule = (moduleName) => {
      if (!moduleName || !isAuthenticated) return false;

      console.log(`🔐 useAuth: Checking module access for "${moduleName}"`, {
        userRole,
        effectiveModules,
        userPermissions: normalizedPermissions
      });

      // 🚀 ÖZEL DURUM: User rolü için Modules claim'i yoksa, permission'lardan modül erişimini çıkar
      // Bu, backend'de Modules claim'i eksik olan durumlarda geçici çözümdür
      if (userRole === ROLES.USER) {
        const hasModulePermission = normalizedPermissions.some(p => {
          const permModule = p.split('.')[0];
          return permModule === moduleName.toLowerCase();
        });
        console.log(`🔐 useAuth: User permission-based module access for "${moduleName}":`, {
          hasModulePermission,
          userPermissions: normalizedPermissions,
          moduleName: moduleName.toLowerCase(),
          note: 'Modules claim not required for User role - using permissions directly'
        });
        return hasModulePermission;
      }

      // Admin ve TenantAdmin için Modules claim'i zorunlu
      // 1. Adım: Tenant bu modüle sahip mi? (Token'dan gelen modüller kullanılıyor)
      const isModuleEnabledForTenant = effectiveModules && effectiveModules.some(module => 
        module.toLowerCase() === moduleName.toLowerCase()
      );
      if (!isModuleEnabledForTenant) {
        console.log(`🔐 useAuth: Module "${moduleName}" NOT available in effective modules:`, effectiveModules);
        return false;
      }

      // 2. Adım: Admin ve TenantAdmin, modül tenant'ta aktifse erişebilir
      if (userRole === ROLES.ADMIN || userRole === ROLES.TENANT_ADMIN) {
        console.log(`🔐 useAuth: Admin/TenantAdmin access granted for module "${moduleName}"`);
        return true;
      }
      
      return false;
    };

    /**
     * Check if user can read from a module
     * Combines module access with specific read permission
     * @param {string} module - Module name
     * @returns {boolean}
     */
    const canRead = (module) => {
      // First check if user can access the module at all
      if (!canAccessModule(module)) return false;
      
      // Admin and TenantAdmin have read access if they can access the module
      if (userRole === ROLES.ADMIN || userRole === ROLES.TENANT_ADMIN) {
        return true;
      }
      
      // User needs specific read permission
      return hasPermission(`${module}.read`) || hasPermission(`${module}${PERMISSION_FORMAT.WILDCARD}`);
    };

    /**
     * Check if user can write to a module
     * Combines module access with specific write permission
     * @param {string} module - Module name
     * @returns {boolean}
     */
    const canWrite = (module) => {
      // First check if user can access the module at all
      if (!canAccessModule(module)) return false;
      
      // Admin and TenantAdmin have write access if they can access the module
      if (userRole === ROLES.ADMIN || userRole === ROLES.TENANT_ADMIN) {
        return true;
      }
      
      // User needs specific write permission
      return hasPermission(`${module}.write`) || hasPermission(`${module}${PERMISSION_FORMAT.WILDCARD}`);
    };

    /**
     * Check if user can delete from a module
     * Combines module access with specific delete permission
     * @param {string} module - Module name
     * @returns {boolean}
     */
    const canDelete = (module) => {
      // First check if user can access the module at all
      if (!canAccessModule(module)) return false;
      
      // Admin and TenantAdmin have delete access if they can access the module
      if (userRole === ROLES.ADMIN || userRole === ROLES.TENANT_ADMIN) {
        return true;
      }
      
      // User needs specific delete permission
      return hasPermission(`${module}.delete`) || hasPermission(`${module}${PERMISSION_FORMAT.WILDCARD}`);
    };

    /**
     * Check user role
     * @param {string} role - Role to check
     * @returns {boolean}
     */
    const hasRole = (role) => {
      return userRole === role;
    };

    /**
     * Check if user has any of specified roles
     * @param {string[]} roles - Array of roles to check
     * @returns {boolean}
     */
    const hasAnyRole = (roles = []) => {
      return Array.isArray(roles) && roles.includes(userRole);
    };

    /**
     * [ROTA KORUMA FONKSİYONU]
     * Bir rotanın erişilebilir olup olmadığını kontrol eder.
     */
    const canAccessRoute = (config = {}) => {
      const { allowedRoles = [], requiredModule = null, requiredPermissions = [], requireAny = false } = config;

      if (!isAuthenticated) return false;
      if (allowedRoles.length > 0 && !allowedRoles.includes(userRole)) return false;

      // Modül kontrolü (tüm roller için geçerli)
      if (requiredModule && !canAccessModule(requiredModule)) return false;

      // Admin ve TenantAdmin, modül kontrolünden geçtiyse erişebilir
      if (userRole === ROLES.ADMIN || userRole === ROLES.TENANT_ADMIN) return true;
      
      // User için detaylı izin kontrolü
      if (userRole === ROLES.USER && requiredPermissions.length > 0) {
        return requireAny ? hasAnyPermission(requiredPermissions) : hasAllPermissions(requiredPermissions);
      }

      return true;
    };

    /**
     * Menu item visibility check
     * @param {Object} menuConfig - Menu item configuration
     * @returns {boolean}
     */
    const shouldShowMenuItem = (menuConfig = {}) => canAccessRoute(menuConfig);

    /**
     * Get user's accessible modules
     * @returns {string[]}
     */
    const getAccessibleModules = () => {
      if (userRole === ROLES.ADMIN || userRole === ROLES.TENANT_ADMIN) {
        return Object.keys(MODULE_PERMISSIONS);
      }

      return Object.keys(MODULE_PERMISSIONS).filter(module => canAccessModule(module));
    };

    // Default rota belirleme - Tüm roller için dashboard
    const getDefaultRoute = () => {
      if (!isAuthenticated) return '/login';
      // Tüm roller için unified dashboard
      return '/dashboard';
    };

    return {
      user, userRole, isAuthenticated, 
      permissions: normalizedPermissions, 
      tenantModules: effectiveModules, // Use token-based modules
      modules: effectiveModules, // Alias for consistency
      tokenData, // Expose raw token data for debugging
      hasPermission, hasAnyPermission, hasAllPermissions,
      canAccessModule, canAccessRoute,
      canWrite, canRead, canDelete,
      getDefaultRoute,
      hasRole, hasAnyRole,
      getAccessibleModules,
      isAdmin: () => userRole === ROLES.ADMIN,
      isTenantAdmin: () => userRole === ROLES.TENANT_ADMIN,
      isUser: () => userRole === ROLES.USER,
      shouldShowMenuItem
    };
  }, [user, userRole, isAuthenticated, normalizedPermissions, effectiveModules, tokenData]);

  return auth;
};

export default useAuth;
